import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { createSingleRunner } from '@nzyme/utils/createSingleRunner.js';
import { AuthStore } from '@sigma/client/auth/AuthStore.js';
import { GuestCreateEndpoint } from '@sigma/patient-api/guests/GuestCreateEndpoint.js';

import { ApiClient } from '../services/ApiClient.js';
import { AuthGuestStore } from './AuthGuestStore.js';

/**
 * Provider service for initializing guest authentication.
 * Bridges AuthGuestStore and ApiClient to avoid circular dependency.
 */
export const AuthGuestProvider = defineService({
    name: 'AuthGuestProvider',
    deps: {
        apiClient: ApiClient,
        authStore: AuthStore,
        authGuestStore: AuthGuestStore,
        logger: Logger,
    },
    setup({ apiClient, authStore, authGuestStore, logger }) {
        const initializeRunner = createSingleRunner({ handler: doInitialize });

        return {
            initialize,
        };

        /**
         * Initialize guest identity if not authenticated and no guest exists.
         */
        async function initialize() {
            await initializeRunner.execute();
        }

        async function doInitialize() {
            await authStore.checkAuth();
            if (authStore.auth) {
                return;
            }

            if (authGuestStore.guestId) {
                return;
            }

            logger.debug('Claiming new guest identity...');

            const result = await apiClient(GuestCreateEndpoint, undefined, { auth: false });

            authGuestStore.setGuest(result.guestId, result.token);

            logger.debug('Guest identity claimed', { guestId: result.guestId });
        }
    },
});
